import React, { useMemo } from "react";
import {
  Card,
  Input,
  Segmented,
  Spin,
  Empty,
  Checkbox,
  Typography,
  Tag,
  Avatar,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

export function StepSelectPatients({
  loading,
  patients,
  search,
  onSearch,
  sexo,
  onSexo,
  selectedPatientIds,
  onTogglePatient,
  onToggleManyPatients,
}) {
  // "Paciente" (el nombre de respaldo) sale de common: es el rol, no una cadena
  // de esta pantalla.
  const { t } = useTranslation(["therapy", "common"]);

  const currentPageIds = useMemo(
    () => (patients || []).map((p) => p._id),
    [patients],
  );

  const allChecked =
    currentPageIds.length > 0 &&
    currentPageIds.every((id) => selectedPatientIds.includes(id));

  const indeterminate =
    !allChecked && selectedPatientIds.some((id) => currentPageIds.includes(id));

  return (
    <div>
      <div className="revi-wizard-patients__toolbar">
        <Input
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          allowClear
          prefix={<SearchOutlined />}
          placeholder={t("wizard.pacientes.buscarPlaceholder")}
        />

        <Segmented
          value={sexo}
          onChange={onSexo}
          options={[
            { label: t("wizard.pacientes.sexo.todos"), value: "ALL" },
            { label: t("wizard.pacientes.sexo.hombre"), value: "M" },
            { label: t("wizard.pacientes.sexo.mujer"), value: "F" },
            { label: t("wizard.pacientes.sexo.otro"), value: "O" },
          ]}
        />
      </div>

      <div className="revi-wizard-patients__bulk">
        <Checkbox
          checked={allChecked}
          indeterminate={indeterminate}
          onChange={(e) =>
            onToggleManyPatients(currentPageIds, e.target.checked)
          }
        >
          {t("wizard.pacientes.seleccionarTodos")}
        </Checkbox>

        {/*
          La etiqueta y el numero son dos nodos distintos, no una frase pegada:
          se traduce la etiqueta y el numero se queda en su <b>.
        */}
        <Text type="secondary">
          {t("wizard.pacientes.seleccionados")}{" "}
          <b>{selectedPatientIds.length}</b>
        </Text>
      </div>

      {loading ? (
        <div className="revi-wizard-patients__loading">
          <Spin size="large" />
        </div>
      ) : patients?.length ? (
        <div className="revi-wizard-patients__grid">
          {patients.map((p) => {
            const checked = selectedPatientIds.includes(p._id);
            const fullName = `${p?.name || ""} ${p?.surname || ""}`.trim();
            const fallbackLetter = (p?.name?.[0] || "?").toUpperCase();

            return (
              <Card
                key={p._id}
                variant="outlined"
                className={`revi-patient-select-card ${checked ? "is-selected" : ""}`}
                onClick={() => onTogglePatient(p._id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    onTogglePatient(p._id);
                }}
              >
                <div className="revi-patient-select-card__top">
                  <div className="revi-patient-select-card__left">
                    <Avatar
                      size={44}
                      src={p?.avatar || undefined}
                      alt={fullName}
                      className="revi-patient-select-card__avatar"
                    >
                      {fallbackLetter}
                    </Avatar>

                    <div className="revi-patient-select-card__text">
                      <div
                        className="revi-patient-select-card__name"
                        title={fullName}
                      >
                        {fullName || t("common:roles.patient")}
                      </div>

                      <div className="revi-patient-select-card__meta">
                        <Tag>{p?.sexo || "—"}</Tag>
                      </div>
                    </div>
                  </div>

                  <Checkbox checked={checked} readOnly />
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Empty description={t("wizard.pacientes.vacio")} />
      )}
    </div>
  );
}
